import React, { useState, useEffect } from 'react';
import { Modal, Card, Form, Input, Select, DatePicker, Button, Tooltip, message, Row, Col } from 'antd';
import { EditOutlined, LoadingOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { startCase, debounce } from 'lodash';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import moment from 'moment';
import { useUserContext } from '../../../../../core/components/users/useUserContext';
import { ENABLED_USER_FILTER } from '../../../../../core/utils/enabledUserFilter';
import '../../styles/index.scss';

const { Option } = Select;
const { TextArea } = Input;

const ProjectSettingsModal = ({ visible, onCancel, project, currentUser, onSave }) => {
  const [form] = Form.useForm();
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [isEditingTeam, setIsEditingTeam] = useState(false);
  const [isSavingInfo, setIsSavingInfo] = useState(false);
  const [isSavingTeam, setIsSavingTeam] = useState(false);

  const {
    fetchBasicUsersDetails,
    basicUsersDetails,
    basicUsersDetailsLoading,
  } = useUserContext();

  useEffect(() => {
    if (project && visible) {
      form.setFieldsValue({
        name: project.name,
        description: project.description,
        status: project.status,
        priority: project.priority,
        startDate: project.startDate ? moment(project.startDate) : null,
        endDate: project.endDate ? moment(project.endDate) : null,
        owner: project.owner ? project.owner.id : null,
        team: project.team ? project.team.map((member) => member.id) : [],
      });
    }
  }, [project, visible, form]);

  useEffect(() => {
    if (visible) {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const fetchUsers = debounce((name = '') => {
    if (!currentUser) return;

    fetchBasicUsersDetails({
      variables: {
        first: 100,
        skip: 0,
        where: {
          AND: [
            {
              roles_some: {
                org: {
                  id: currentUser.role.org.id,
                  level: 'PLATFORM',
                },
                roleItems_some: {
                  feature: 'TASK_MANAGEMENT',
                },
              },
              name_contains: name,
            },
            ENABLED_USER_FILTER,
          ],
        },
      },
    });
  }, 500);

  const handleSaveInfo = async () => {
    try {
      setIsSavingInfo(true);
      const values = await form.validateFields(['name', 'description', 'status', 'priority', 'startDate', 'endDate', 'owner']);
      await onSave({
        ...values,
        startDate: values.startDate ? values.startDate.toISOString() : null,
        endDate: values.endDate ? values.endDate.toISOString() : null,
      });
      setIsEditingInfo(false);
      message.success('Project information updated successfully.');
    } catch (error) {
      message.error('Failed to update project information.');
      console.error('Update failed:', error);
    } finally {
      setIsSavingInfo(false);
    }
  };

  const handleSaveTeam = async () => {
    try {
      setIsSavingTeam(true);
      const values = await form.validateFields(['team']);
      await onSave({ team: values.team });
      setIsEditingTeam(false);
      message.success('Team members updated successfully.');
    } catch (error) {
      message.error('Failed to update team members.');
      console.error('Update failed:', error);
    } finally {
      setIsSavingTeam(false);
    }
  };

  const handleCancel = () => {
    setIsEditingInfo(false);
    setIsEditingTeam(false);
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      className="task-create-modal"
      title="Project Settings"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={800}
      centered
    >
      <div className="project-settings__container">
        {/* Project Information Card */}
        <Card
          title={
            <div className="project-settings__card-header">
              <Text strong className="project-settings__card-title">Project Information</Text>
              {!isEditingInfo ? (
                <EditOutlined
                  className="project-settings__edit-icon"
                  onClick={() => setIsEditingInfo(true)}
                />
              ) : (
                <div className="project-settings__actions">
                  <Button
                    type="primary"
                    onClick={handleSaveInfo}
                    disabled={isSavingInfo}
                    className="project-settings__btn-save"
                  >
                    {isSavingInfo && <LoadingOutlined />} Save
                  </Button>
                  <Button
                    onClick={() => {
                      setIsEditingInfo(false);
                      form.setFieldsValue({
                        name: project.name,
                        description: project.description,
                        status: project.status,
                        priority: project.priority,
                        startDate: project.startDate ? moment(project.startDate) : null,
                        endDate: project.endDate ? moment(project.endDate) : null,
                        owner: project.owner ? project.owner.id : null,
                      });
                    }}
                    disabled={isSavingInfo}
                    className="project-settings__btn-cancel"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          }
          className="project-settings__card"
        >
          <Form form={form} layout="vertical">
            {isEditingInfo ? (
              <>
                <Form.Item
                  label="Project Name"
                  name="name"
                  rules={[{ required: true, message: 'Please enter project name' }]}
                >
                  <Input placeholder="Enter project name" className="custom-input" />
                </Form.Item>

                <Form.Item label="Description" name="description">
                  <TextArea rows={3} placeholder="Enter project description" className="custom-textarea" />
                </Form.Item>

                <Form.Item label="Owner" name="owner">
                  <Select
                    placeholder="Select project owner"
                    showSearch
                    loading={basicUsersDetailsLoading}
                    onSearch={fetchUsers}
                    onFocus={() => fetchUsers()}
                    filterOption={false}
                    allowClear
                    className="custom-select"
                    onChange={(selectedOwnerId) => {
                      const currentTeam = form.getFieldValue('team') || [];
                      if (selectedOwnerId && currentTeam.includes(selectedOwnerId)) {
                        message.destroy();
                        message.warning('User removed from team members as they are now the owner.');
                        form.setFieldsValue({
                          team: currentTeam.filter((id) => id !== selectedOwnerId),
                        });
                      }
                    }}
                  >
                    {basicUsersDetails.map((user) => (
                      <Option key={user.id} value={user.id}>
                        {startCase(user.name || user.username)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Status" name="status">
                      <Select placeholder="Select status" className="custom-select">
                        <Option value="NOT_STARTED">Not Started</Option>
                        <Option value="PLANNING">Planning</Option>
                        <Option value="IN_PROGRESS">In Progress</Option>
                        <Option value="COMPLETED">Completed</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Priority" name="priority">
                      <Select placeholder="Select priority" className="custom-select">
                        <Option value="LOW">Low</Option>
                        <Option value="MEDIUM">Medium</Option>
                        <Option value="HIGH">High</Option>
                        <Option value="CRITICAL">Critical</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Start Date" name="startDate">
                      <DatePicker className="custom-date task-w--full" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="End Date" name="endDate">
                      <DatePicker className="custom-date task-w--full" />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            ) : (
              <div className="project-settings__info-list">
                <div>
                  <Text strong className="project-settings__info-label">Project Name</Text>
                  <Text className="project-settings__info-value">{project?.name || '-'}</Text>
                </div>
                <div>
                  <Text strong className="project-settings__info-label">Description</Text>
                  <Text className="project-settings__info-value">{project?.description || 'No description provided'}</Text>
                </div>
                <div>
                  <Text strong className="project-settings__info-label">Owner</Text>
                  {project?.owner ? (
                    <div className="project-settings__owner-row">
                      <AvatarS3
                        imgKey={project.owner.avatar && project.owner.avatar.key}
                        username={project.owner.name || project.owner.username}
                        avatarProps={{ size: 24 }}
                      />
                      <Text className="project-settings__info-value">
                        {startCase(project.owner.name || project.owner.username)}
                      </Text>
                    </div>
                  ) : (
                    <Text className="project-settings__info-value">No owner assigned</Text>
                  )}
                </div>
                <Row gutter={16}>
                  <Col span={12}>
                    <Text strong className="project-settings__info-label">Status</Text>
                    <Text className="project-settings__info-value">
                      {project?.status
                        ? project.status.split('_').map(word => word.charAt(0) + word.slice(1).toLowerCase()).join(' ')
                        : '-'}
                    </Text>
                  </Col>
                  <Col span={12}>
                    <Text strong className="project-settings__info-label">Priority</Text>
                    <Text className="project-settings__info-value">
                      {project?.priority ? startCase(project.priority.toLowerCase()) : '-'}
                    </Text>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Text strong className="project-settings__info-label">Start Date</Text>
                    <Text className="project-settings__info-value">
                      {project?.startDate ? moment(project.startDate).format('MMM D, YYYY') : '-'}
                    </Text>
                  </Col>
                  <Col span={12}>
                    <Text strong className="project-settings__info-label">End Date</Text>
                    <Text className="project-settings__info-value">
                      {project?.endDate ? moment(project.endDate).format('MMM D, YYYY') : '-'}
                    </Text>
                  </Col>
                </Row>
              </div>
            )}
          </Form>
        </Card>

        {/* Team Members Card */}
        <Card
          title={
            <div className="project-settings__card-header">
              <Text strong className="project-settings__card-title">Team Members</Text>
              {!isEditingTeam ? (
                <EditOutlined
                  className="project-settings__edit-icon"
                  onClick={() => setIsEditingTeam(true)}
                />
              ) : (
                <div className="project-settings__actions">
                  <Button
                    type="primary"
                    onClick={handleSaveTeam}
                    disabled={isSavingTeam}
                    className="project-settings__btn-save"
                  >
                    {isSavingTeam && <LoadingOutlined />} Save
                  </Button>
                  <Button
                    onClick={() => {
                      setIsEditingTeam(false);
                      form.setFieldsValue({
                        team: project.team ? project.team.map((member) => member.id) : [],
                      });
                    }}
                    disabled={isSavingTeam}
                    className="project-settings__btn-cancel"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          }
          className="project-settings__card"
        >
          <Form form={form} layout="vertical">
            {isEditingTeam ? (
              <Form.Item label="Select Team Members" name="team">
                <Select
                  mode="multiple"
                  placeholder="Select team members"
                  loading={basicUsersDetailsLoading}
                  onSearch={fetchUsers}
                  onFocus={() => fetchUsers()}
                  filterOption={false}
                  style={{ width: '100%' }}
                  onChange={(selectedIds) => {
                    const ownerId = form.getFieldValue('owner');
                    if (ownerId && selectedIds.includes(ownerId)) {
                      message.destroy();
                      message.warning('Owner cannot be added as a team member.');
                      form.setFieldsValue({
                        team: selectedIds.filter((id) => id !== ownerId),
                      });
                    }
                  }}
                >
                  {basicUsersDetails.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {startCase(user.name || user.username)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            ) : null}

            <div>
              {project && project.team && project.team.length > 0 ? (
                <div className="project-settings__team-grid">
                  {project.team.map((member) => (
                    <Tooltip key={member.id} title={startCase(member.name || member.username)}>
                      <div className="project-settings__team-member">
                        <AvatarS3
                          imgKey={member.avatar && member.avatar.key}
                          username={member.name || member.username}
                          avatarProps={{ size: 40 }}
                        />
                      </div>
                    </Tooltip>
                  ))}
                </div>
              ) : (
                <Text className="project-settings__info-value">No team members assigned</Text>
              )}
            </div>
          </Form>
        </Card>
      </div>
    </Modal>
  );
};

export default ProjectSettingsModal;
